/*
 * Modern Britwear — cart drawer cross-sell mount point.
 *
 * Loaded unconditionally via <script src="{{ 'mbw-cart-upsell.js' | asset_url }}">
 * (not an inline <script> tag) because Horizon's drawer morph
 * (assets/component-cart-items.js -> morphSection) can re-render the markup
 * this element sits inside without ever removing the element itself. An inline
 * <script> block only runs once, on first parse, so any add-to-cart after the
 * first would leave the row dead. A script loaded by src re-registers the
 * customElements.define() call fine (it's guarded), but more importantly the
 * element's own lifecycle (connectedCallback / the cart:update listener) is
 * what does the refreshing, not the script tag.
 */
(function () {
  if (customElements.get('mbw-cart-cross-sell')) return;

  customElements.define(
    'mbw-cart-cross-sell',
    class extends HTMLElement {
      connectedCallback() {
        this.base = (this.dataset.root || '/').replace(/\/$/, '');
        this.requestId = 0;
        this.onClick = this.onClick.bind(this);
        this.onCartUpdate = this.onCartUpdate.bind(this);
        this.addEventListener('click', this.onClick);
        // Bound broadly: this store's own mbw_complete_look.liquid block
        // dispatches 'cart:add', the Horizon cart-items-component dispatches
        // its own CartAddEvent/CartLinesUpdateEvent classes (whose exact
        // `type` string varies by theme build), and some Horizon releases fire
        // 'shopify:cart:lines-update' or plain 'cart:update'. Listening for
        // all of them (rather than picking one) is what keeps this row alive
        // across an add/update from any source, not just its own button.
        [
          'cart:update',
          'cart:add',
          'cart:change',
          'shopify:cart:lines-update',
          'shopify:cart:update',
        ].forEach((name) => document.addEventListener(name, this.onCartUpdate));
        this.load();
      }

      disconnectedCallback() {
        this.removeEventListener('click', this.onClick);
        [
          'cart:update',
          'cart:add',
          'cart:change',
          'shopify:cart:lines-update',
          'shopify:cart:update',
        ].forEach((name) => document.removeEventListener(name, this.onCartUpdate));
      }

      async onCartUpdate(event) {
        let cart = event && event.detail && (event.detail.resource || event.detail.cart);
        if (!cart || !Array.isArray(cart.items)) {
          try {
            cart = await (await fetch(this.base + '/cart.js')).json();
          } catch (error) {
            return;
          }
        }

        const items = cart.items || [];
        if (!items.length) {
          this.innerHTML = '';
          this.hidden = true;
          return;
        }

        this.dataset.productId = items[items.length - 1].product_id;
        this.load();
      }

      async load() {
        const productId = this.dataset.productId;
        if (!productId) return;

        const requestId = ++this.requestId;

        const markup =
          (await this.fetchSection(productId, 'complementary')) ||
          (await this.fetchSection(productId, 'related'));

        if (requestId !== this.requestId) return;

        if (!markup) {
          this.innerHTML = '';
          this.hidden = true;
          return;
        }

        this.innerHTML = markup;
        this.hidden = false;
      }

      async fetchSection(productId, intent) {
        const url =
          this.base +
          '/recommendations/products?section_id=mbw_cart_upsell&limit=10&product_id=' +
          encodeURIComponent(productId) +
          '&intent=' +
          intent;

        try {
          const response = await fetch(url);
          if (!response.ok) return null;

          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const root = doc.querySelector('[data-mbw-upsell-root]');
          return root ? root.outerHTML : null;
        } catch (error) {
          return null;
        }
      }

      async onClick(event) {
        const button = event.target.closest('button[data-mbw-add]');
        if (!button) return;

        const row = button.closest('.mbw-upsell__item');
        const select = row && row.querySelector('[data-mbw-size]');
        const variantId = Number(button.dataset.variantId || (select && select.value));
        if (!variantId) return;

        button.setAttribute('aria-busy', 'true');

        try {
          const sectionIds = Array.from(
            document.querySelectorAll('cart-items-component[data-section-id]')
          )
            .map((el) => el.dataset.sectionId)
            .filter((id, index, all) => id && all.indexOf(id) === index);

          const addResponse = await fetch(this.base + '/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({
              items: [{ id: variantId, quantity: 1 }],
              sections: sectionIds.join(','),
              sections_url: window.location.pathname,
            }),
          });

          if (!addResponse.ok) {
            button.removeAttribute('aria-busy');
            return;
          }

          const addResult = await addResponse.json();

          // Swap the same cart sections mbw_complete_look.liquid swaps, so the
          // drawer item list and totals update the same way an ordinary
          // add-to-cart does, without waiting on Horizon's own morph cycle.
          if (addResult && addResult.sections) {
            Object.keys(addResult.sections).forEach((sectionId) => {
              const html = addResult.sections[sectionId];
              if (!html) return;
              const parsed = new DOMParser().parseFromString(html, 'text/html');
              const liveEl =
                document.getElementById('shopify-section-' + sectionId) ||
                document.querySelector('[data-section-id="' + sectionId + '"]');
              const freshEl =
                parsed.getElementById('shopify-section-' + sectionId) ||
                parsed.querySelector('[data-section-id="' + sectionId + '"]') ||
                parsed.body.firstElementChild;
              if (liveEl && freshEl) liveEl.innerHTML = freshEl.innerHTML;
            });
          }

          // Stamp the cart the same way the PDP's Complete the Look block does
          // (with its own distinct key), so cross-sell attribution stays
          // measurable per-surface.
          const cart = await (
            await fetch(this.base + '/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ attributes: { _mbw_cross_sell_cart: 'cart-drawer' } }),
            })
          ).json();

          // This theme's actual header badge markup (verified against the live
          // preview before shipping — see build notes): a `.cart-bubble` wrapper
          // with `.cart-bubble__text` / `.cart-bubble__text-count` spans holding
          // the visible digits, plus a `.visually-hidden` span for the a11y
          // count. Generic `[data-cart-count]`/`.cart-count` selectors do not
          // exist in this theme and silently matched nothing.
          document
            .querySelectorAll('.cart-bubble__text, .cart-bubble__text-count, [data-cart-count], .cart-count, .cart-count-bubble')
            .forEach((el) => {
              el.textContent = cart.item_count;
              el.classList.remove('hidden');
            });
          document.querySelectorAll('.cart-bubble').forEach((el) => {
            el.classList.toggle('visually-hidden', cart.item_count === 0);
          });

          document.dispatchEvent(
            new CustomEvent('cart:update', {
              bubbles: true,
              detail: { resource: cart, sourceId: 'mbw-cart-upsell', data: { itemCount: cart.item_count } },
            })
          );

          this.dataset.productId = cart.items.length ? cart.items[cart.items.length - 1].product_id : '';
          this.load();
        } catch (error) {
          // A failed upsell add is not worth breaking the drawer over.
        } finally {
          button.removeAttribute('aria-busy');
        }
      }
    }
  );
})();
